/**
 * useAuth — hook centrale per stato auth + ruolo + freshness MFA.
 *
 * Responsabilità:
 * - Ascolta `onAuthStateChanged` e `onIdTokenChanged` di Firebase Auth.
 * - Estrae il custom claim `role` (stringa singola, vedi
 *   .cursor/rules/firestore-rules-rbac.mdc) da `getIdTokenResult`.
 * - Calcola lo `mfaTier` corrente in base ad `auth_time` del token.
 * - Sottoscrive il documento `/users/{uid}` per `mustChangePasswordAtNextLogin`
 *   e `mfaEnrolled`.
 *
 * NOTA SICUREZZA:
 * - Il claim `role` può essere settato SOLO server-side (Cloud Function con
 *   Admin SDK). Mai dal client.
 * - L'mfaTier qui calcolato è puramente UX: le Firestore Rules e i
 *   `assertTier1/2` server-side restano gli unici enforcement reali.
 *
 * ARCHITETTURA — SINGLETON STATE:
 * - Lo stato (`user`, `tokenState`, `claimsReady`, `userDoc`, ecc.) vive
 *   a livello modulo, NON per-istanza dell'hook. I listener Firebase
 *   vengono registrati una sola volta al primo uso.
 * - Ogni componente che chiama `useAuth()` si limita a iscriversi
 *   alle notifiche di cambio stato; legge i valori dal singleton.
 * - Motivazione: prima del refactor, ogni componente con `useAuth()`
 *   creava la propria copia di state (role=null all'inizio), causando
 *   redirect spuri sulle pagine role-gated tipo /impostazioni/partite-iva
 *   prima che `getIdTokenResult` risolvesse (~10ms dopo il mount). Ora
 *   il primo `useAuth()` (es. RouteGuard alla root) inizializza il
 *   singleton; i `useAuth()` successivi nei figli leggono lo state già
 *   popolato. Vedi docs/known-issues.md sezione routing.
 */

import { useEffect, useState, useMemo } from 'react';
import {
  onAuthStateChanged,
  onIdTokenChanged,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, firestore } from '../security/firebase';
import {
  MFA_TIER1_MAX_AGE_SEC,
  MFA_TIER2_MAX_AGE_SEC,
} from '../security/constants';
import type { AuthState, MfaTier, UserRole, UserDoc } from '@shared/types/auth';
import {
  BACKOFFICE_CLAIM,
  BACKOFFICE_DELEGA_CLAIM,
  parseDelegaClaim,
} from '@shared/auth/backoffice';

const VALID_ROLES: ReadonlyArray<UserRole> = [
  'TITOLARE',
  'DIETOLOGA',
  'SEGRETERIA',
  'ADMIN',
  'PAZIENTE',
];

/**
 * Calcolo puro: ritorna il tier MFA in base ad auth_time (secondi epoch).
 * - Tier 2: <= 5 minuti
 * - Tier 1: <= 12 ore
 * - Tier 0: oltre, o auth_time mancante.
 *
 * Esportato a parte per test puri.
 */
export function computeMfaTier(
  authTimeSec: number | null | undefined,
  nowSec: number = Math.floor(Date.now() / 1000)
): MfaTier {
  if (!authTimeSec || authTimeSec <= 0) return 0;
  const ageSec = nowSec - authTimeSec;
  if (ageSec < 0) return 2;
  if (ageSec <= MFA_TIER2_MAX_AGE_SEC) return 2;
  if (ageSec <= MFA_TIER1_MAX_AGE_SEC) return 1;
  return 0;
}

function parseRoleClaim(value: unknown): UserRole | null {
  if (typeof value !== 'string') return null;
  return (VALID_ROLES as ReadonlyArray<string>).includes(value)
    ? (value as UserRole)
    : null;
}

interface InternalTokenState {
  role: UserRole | null;
  authTimeSec: number | null;
  /**
   * PR-BO: claim `bo`, provisionato server-side dall'email (prefisso `b.`).
   * Non si guarda l'email del token: un utente autenticato puo' cambiarla
   * via API REST Identity Toolkit col suo ID token e guadagnarsi il
   * prefisso. Vedi src/shared/auth/backoffice.ts.
   */
  isBackoffice: boolean;
  /**
   * PR-BO-DELEGA: claim `bodeleg`, uid della dietologa delegante. Valido
   * solo per gli account di backoffice: le Rules ignorano la delega senza
   * il claim `bo`, e qui la scartiamo per la stessa ragione, cosi' il
   * client non costruisce query che il server rifiuterebbe.
   */
  delegaDietologaUid: string | null;
}

const EMPTY_TOKEN: InternalTokenState = {
  role: null,
  authTimeSec: null,
  isBackoffice: false,
  delegaDietologaUid: null,
};

/** Il claim arriva come booleano; qualunque altra forma = non backoffice. */
function parseBackofficeClaim(value: unknown): boolean {
  return value === true;
}

function tokenStateFromClaims(claims: Record<string, unknown>): InternalTokenState {
  const isBackoffice = parseBackofficeClaim(claims[BACKOFFICE_CLAIM]);
  return {
    role: parseRoleClaim(claims.role),
    authTimeSec: claims.auth_time ? Number(claims.auth_time) : null,
    isBackoffice,
    delegaDietologaUid: isBackoffice
      ? parseDelegaClaim(claims[BACKOFFICE_DELEGA_CLAIM])
      : null,
  };
}

/**
 * Scope per le query che filtrano su `dietologaId`.
 *
 * Null per ogni ruolo diverso da DIETOLOGA: quei ruoli sono studio-wide e
 * la query non deve portare alcun vincolo. Per la DIETOLOGA e' l'uid
 * delegante se presente, altrimenti il proprio: e' lo stesso valore che
 * `dietologaScopeUid()` calcola nelle Firestore Rules, e le due parti
 * devono coincidere o la query viene rifiutata.
 */
export function computeDietologaScopeUid(
  role: UserRole | null,
  uid: string | null | undefined,
  delegaUid: string | null
): string | null {
  if (role !== 'DIETOLOGA') return null;
  return delegaUid ?? uid ?? null;
}

/**
 * Uid con cui valutare l'appartenenza di un dato lato UI.
 *
 * Come `computeDietologaScopeUid`, ma senza annullarsi sui ruoli
 * studio-wide: dove il confronto va fatto comunque (`nota.createdBy`,
 * `allegato.uploadedBy`, `paziente.dietologaId`) serve un uid, e per una
 * TITOLARE e' semplicemente il proprio.
 */
export function computeUidOperativo(
  role: UserRole | null,
  uid: string | null | undefined,
  delegaUid: string | null
): string | null {
  if (role === 'DIETOLOGA' && delegaUid !== null) return delegaUid;
  return uid ?? null;
}

/**
 * Timeout di sicurezza per il fallback di `claimsReady`. Se il primo
 * `onIdTokenChanged` callback non arriva entro questo intervallo
 * (es. App Check 403, network down, race con logout immediato),
 * sblocchiamo comunque `loading` per evitare freeze UI permanente.
 *
 * Trade-off scelto: 3000ms copre il 95p delle reti italiane mobile,
 * basso abbastanza da non frustrare l'utente. Le rules server-side
 * restano l'unica sorgente di verita' di sicurezza.
 */
const CLAIMS_FALLBACK_MS = 3000;

export interface UseAuthReturn extends AuthState {
  authTimeSec: number | null;
  /**
   * Forza `getIdTokenResult(true)` e propaga il nuovo `auth_time` nello
   * stato singleton. Best-effort: se non c'e' utente loggato, ritorna
   * silenziosamente. Mantiene retro-compatibilita' con i caller esistenti
   * (RouteGuard post-change-password, ecc.).
   */
  refreshToken: () => Promise<void>;
  /**
   * Variante usata DOPO una reautenticazione reale
   * (`reauthenticateWithCredential` + eventuale TOTP assertion) in
   * `MfaModal`. Differenze rispetto a `refreshToken`:
   *  - lancia se non c'e' un utente loggato (vuole semantica fail-fast:
   *    se chiami questa funzione DEVI avere appena step-uppato);
   *  - aggiorna `singletonState.tokenState` con `auth_time` aggiornato
   *    e notifica tutti i subscribers, cosi' la pill MFA + i gate
   *    Tier 2 vedono immediatamente il nuovo tier.
   */
  refreshTokenAndAssert: () => Promise<void>;
}

// =====================================================================
// SINGLETON STATE
// Tutto lo stato condiviso da ogni `useAuth()` vive qui. I componenti
// si iscrivono via `subscribers` e ricevono `notify()` ad ogni cambio.
// =====================================================================

interface SingletonState {
  user: FirebaseUser | null;
  tokenState: InternalTokenState;
  claimsReady: boolean;
  userDoc: UserDoc | null;
  /**
   * True quando il primo snapshot di `/users/{uid}` è arrivato (esista o
   * no il doc), oppure quando NON c'è utente loggato. Serve a distinguere
   * "userDoc non ancora caricato" da "userDoc caricato = null":
   * `mfaEnrolled`/`mustChangePassword` derivano da `userDoc`, quindi il
   * `RouteGuard` NON deve valutare i redirect MFA/password finché questo
   * flag è false (altrimenti vede `mfaEnrolled=false` transitorio e su
   * cold-boot rimbalza deep-link -> /mfa/enroll -> /app). Vedi
   * docs/known-issues.md sezione routing.
   */
  userDocReady: boolean;
  authLoading: boolean;
  error: Error | null;
}

const singletonState: SingletonState = {
  user: null,
  tokenState: EMPTY_TOKEN,
  claimsReady: false,
  userDoc: null,
  userDocReady: false,
  authLoading: true,
  error: null,
};

const subscribers = new Set<() => void>();

function notifyAll(): void {
  subscribers.forEach((fn) => fn());
}

let listenersInitialized = false;
let userDocUnsub: (() => void) | null = null;
let claimsFallbackTimer: ReturnType<typeof setTimeout> | null = null;
let authUnsub: (() => void) | null = null;
let idTokenUnsub: (() => void) | null = null;

function subscribeUserDoc(uid: string): void {
  if (userDocUnsub) {
    userDocUnsub();
    userDocUnsub = null;
  }
  // Nuova sottoscrizione = stato userDoc non ancora noto per questo utente:
  // finché il primo snapshot non arriva, `loading` resta true (vedi
  // `SingletonState.userDocReady`).
  singletonState.userDocReady = false;
  const ref = doc(firestore, 'users', uid);
  userDocUnsub = onSnapshot(
    ref,
    (snap) => {
      singletonState.userDoc = snap.exists() ? (snap.data() as UserDoc) : null;
      singletonState.userDocReady = true;
      notifyAll();
    },
    (err) => {
      // Anche in errore (es. offline, permessi) sblocchiamo il gate: non
      // possiamo confermare l'enrollment ma non dobbiamo congelare la UI.
      singletonState.error = err as Error;
      singletonState.userDocReady = true;
      notifyAll();
    }
  );
}

function ensureListeners(): void {
  if (listenersInitialized) return;
  listenersInitialized = true;

  authUnsub = onAuthStateChanged(
    auth,
    (next) => {
      singletonState.user = next;
      singletonState.authLoading = false;

      if (next) {
        subscribeUserDoc(next.uid);
      } else {
        if (userDocUnsub) {
          userDocUnsub();
          userDocUnsub = null;
        }
        singletonState.userDoc = null;
        // Nessun utente: nessun userDoc da attendere -> gate aperto.
        singletonState.userDocReady = true;
      }

      // Fallback timer: se claims o userDoc non arrivano entro 3s dopo aver
      // ottenuto un user autenticato, sblocca comunque `loading` (evita
      // freeze UI permanente in caso di App Check 403 / rete down / errore
      // rules). Le rules server-side restano l'unica sorgente di verita'.
      if (claimsFallbackTimer) {
        clearTimeout(claimsFallbackTimer);
        claimsFallbackTimer = null;
      }
      if (
        next &&
        (!singletonState.claimsReady || !singletonState.userDocReady)
      ) {
        claimsFallbackTimer = setTimeout(() => {
          if (!singletonState.claimsReady || !singletonState.userDocReady) {
            console.warn(
              '[useAuth] claims/userDoc non popolati entro 3s dopo ' +
                'onAuthStateChanged: sblocco loading per evitare freeze UI. ' +
                'role/mfaEnrolled potrebbero essere incompleti finche i ' +
                'listener non completano.'
            );
            singletonState.claimsReady = true;
            singletonState.userDocReady = true;
            notifyAll();
          }
        }, CLAIMS_FALLBACK_MS);
      }

      notifyAll();
    },
    (err) => {
      singletonState.error = err as Error;
      singletonState.authLoading = false;
      notifyAll();
    }
  );

  idTokenUnsub = onIdTokenChanged(auth, async (next) => {
    if (!next) {
      singletonState.tokenState = EMPTY_TOKEN;
      singletonState.claimsReady = true;
      notifyAll();
      return;
    }
    try {
      const result = await next.getIdTokenResult();
      singletonState.tokenState = tokenStateFromClaims(result.claims);
    } catch (err) {
      singletonState.error = err as Error;
    } finally {
      singletonState.claimsReady = true;
      notifyAll();
    }
  });
}

/**
 * Aggiorna lo stato singleton dai claims correnti del token Firebase.
 *
 * @param force se true forza `getIdTokenResult(true)` -> Firebase ottiene
 *   un nuovo ID token dal server e propaga eventuali claim cambiati
 *   (incluso `auth_time` dopo `reauthenticateWithCredential`).
 *   Sempre true in tutti i call site reali; il parametro esiste solo
 *   per simmetria.
 *
 * Singleton-level: muta `singletonState.tokenState` e chiama `notifyAll()`.
 * NON crea closure per-istanza, nessun `useCallback` necessario.
 */
async function refreshTokenSingleton(force: boolean): Promise<void> {
  if (!singletonState.user) return;
  try {
    const result = await singletonState.user.getIdTokenResult(force);
    singletonState.tokenState = tokenStateFromClaims(result.claims);
    notifyAll();
  } catch (err) {
    singletonState.error = err as Error;
    notifyAll();
  }
}

/**
 * Variante "post step-up" di refresh token. Lancia se non c'e' utente
 * loggato: il chiamante (MfaModal post-reauth) NON deve mai trovarsi in
 * stato anonimo; se lo e', e' un bug e va sollevato.
 *
 * Vedi `UseAuthReturn.refreshTokenAndAssert` per la semantica.
 */
async function refreshTokenAndAssertSingleton(): Promise<void> {
  if (!singletonState.user) {
    throw new Error(
      'refreshTokenAndAssert: nessun utente loggato. Chiamare solo dopo ' +
        'reauthenticateWithCredential con esito positivo.'
    );
  }
  const result = await singletonState.user.getIdTokenResult(true);
  singletonState.tokenState = tokenStateFromClaims(result.claims);
  notifyAll();
}

/**
 * Reset del singleton — solo per test. NON usare in produzione.
 */
export function __resetAuthSingletonForTests(): void {
  if (authUnsub) {
    authUnsub();
    authUnsub = null;
  }
  if (idTokenUnsub) {
    idTokenUnsub();
    idTokenUnsub = null;
  }
  if (userDocUnsub) {
    userDocUnsub();
    userDocUnsub = null;
  }
  if (claimsFallbackTimer) {
    clearTimeout(claimsFallbackTimer);
    claimsFallbackTimer = null;
  }
  singletonState.user = null;
  singletonState.tokenState = EMPTY_TOKEN;
  singletonState.claimsReady = false;
  singletonState.userDoc = null;
  singletonState.userDocReady = false;
  singletonState.authLoading = true;
  singletonState.error = null;
  subscribers.clear();
  listenersInitialized = false;
}

export function useAuth(): UseAuthReturn {
  ensureListeners();

  // Forza re-render quando il singleton notifica un cambiamento.
  const [, setRenderTick] = useState(0);
  // tick separato per ricalcolare mfaTier ogni 60s (auth_time non
  // cambia ma l'eta' invecchia).
  const [mfaTick, setMfaTick] = useState(0);

  useEffect(() => {
    const cb = () => setRenderTick((n) => (n + 1) | 0);
    subscribers.add(cb);
    // Subito dopo il subscribe, forza un re-render se nel frattempo
    // un'altra istanza ha gia' popolato lo stato (race tra mount).
    cb();
    return () => {
      subscribers.delete(cb);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setMfaTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  const mfaTier = useMemo<MfaTier>(
    () => computeMfaTier(singletonState.tokenState.authTimeSec),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [singletonState.tokenState.authTimeSec, mfaTick]
  );

  return {
    user: singletonState.user,
    role: singletonState.tokenState.role,
    mfaTier,
    authTimeSec: singletonState.tokenState.authTimeSec,
    mustChangePassword:
      singletonState.userDoc?.mustChangePasswordAtNextLogin === true,
    mfaEnrolled: singletonState.userDoc?.mfaEnrolled === true,
    isBackoffice: singletonState.tokenState.isBackoffice,
    dietologaScopeUid: computeDietologaScopeUid(
      singletonState.tokenState.role,
      singletonState.user?.uid,
      singletonState.tokenState.delegaDietologaUid
    ),
    uidOperativo: computeUidOperativo(
      singletonState.tokenState.role,
      singletonState.user?.uid,
      singletonState.tokenState.delegaDietologaUid
    ),
    // Loading "esteso": finché c'e' un user autenticato ma i custom
    // claims OPPURE il documento /users/{uid} non sono stati letti almeno
    // una volta, restiamo in loading. Attendere anche `userDocReady` evita
    // che RouteGuard valuti i redirect MFA/mustChangePassword con
    // `mfaEnrolled=false` transitorio (bug deep-link cold-boot -> home).
    loading:
      singletonState.authLoading ||
      (!!singletonState.user &&
        (!singletonState.claimsReady || !singletonState.userDocReady)),
    error: singletonState.error,
    refreshToken: refreshTokenPublic,
    refreshTokenAndAssert: refreshTokenAndAssertSingleton,
  };
}

/**
 * Wrapper pubblico stabile per `refreshTokenSingleton(true)`. Riferimento
 * di identita' stabile a livello modulo: nessuna `useCallback` necessaria
 * lato hook, e i caller (es. `useMfaStepUp.handleSuccess`) possono
 * memoizzare senza dipendenze.
 */
async function refreshTokenPublic(): Promise<void> {
  return refreshTokenSingleton(true);
}
